import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { useCreateStudentMutation } from '../services/registrationApi';

type LeadSource = 'WEBSITE' | 'QR_CODE';

type FormData = {
  name: string;
  emailId: string;
  college: string;
  phoneNumber: string;
  branch: string;
  leadSource: LeadSource;
};

export type RegistrationData = {
  id: string;
} & FormData;

const RegistrationForm = ({
  onSuccess,
  formData,
  setFormData
}: {
  onSuccess: (data: RegistrationData) => void;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) => {
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
    {}
  );

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.emailId.trim()) {
      newErrors.emailId = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId)) {
      newErrors.emailId = 'Invalid email format';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phoneNumber.replace(/[\s-]/g, ''))) {
      newErrors.phoneNumber = 'Phone number must be 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    try {
      if (validateForm()) {
        const newRegistration: RegistrationData = {
          ...formData,
          id: Date.now().toString()
        };
        onSuccess(newRegistration);

        setErrors({});
      }
    } catch (err) {
      toast.error(
        (err as any)?.data?.message || 'Registration failed. Please try again.'
      );
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className='flex min-h-screen items-center justify-center bg-white p-4'>
      <div className='w-full max-w-md rounded-2xl bg-gradient-to-br from-blue-50/5 p-8 shadow-2xl'>
        <h1 className='mb-8 text-center text-3xl font-bold text-gray-800'>
          Registration Form
        </h1>

        <div className='space-y-5'>
          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700'>
              Name
            </label>
            <input
              type='text'
              name='name'
              value={formData.name}
              onChange={handleChange}
              className='w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-indigo-500'
              placeholder='Enter your name'
            />
          </div>

          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700'>
              Email ID <span className='text-red-500'>*</span>
            </label>
            <input
              type='email'
              name='emailId'
              value={formData.emailId}
              onChange={handleChange}
              className={`w-full rounded-lg border bg-white px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-indigo-500 ${
                errors.emailId ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder='your.email@example.com'
            />
            {errors.emailId && (
              <p className='mt-1 text-sm text-red-500'>{errors.emailId}</p>
            )}
          </div>

          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700'>
              College
            </label>
            <input
              type='text'
              name='college'
              value={formData.college}
              onChange={handleChange}
              className='w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-indigo-500'
              placeholder='Enter your college name'
            />
          </div>

          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700'>
              Phone Number <span className='text-red-500'>*</span>
            </label>
            <input
              type='tel'
              name='phoneNumber'
              value={formData.phoneNumber}
              onChange={handleChange}
              className={`w-full rounded-lg border bg-white px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-indigo-500 ${
                errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder='10-digit phone number'
            />
            {errors.phoneNumber && (
              <p className='mt-1 text-sm text-red-500'>{errors.phoneNumber}</p>
            )}
          </div>

          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700'>
              Branch
            </label>
            <input
              type='text'
              name='branch'
              value={formData.branch}
              onChange={handleChange}
              className='w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-indigo-500'
              placeholder='e.g., CSE, ECE, EEE'
            />
          </div>

          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700'>
              Lead Source
            </label>
            <select
              name='leadSource'
              value={formData.leadSource}
              onChange={handleChange}
              className='w-full rounded-lg border border-gray-300 bg-white bg-white px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-indigo-500'
            >
              <option value='WEBSITE'>Website</option>
              <option value='QR_CODE'>QR Code</option>
            </select>
          </div>

          <button
            onClick={handleSubmit}
            className='w-full transform rounded-lg bg-indigo-600 py-3 font-semibold text-white shadow-lg transition hover:scale-[1.02] hover:bg-indigo-700'
          >
            Register
          </button>
        </div>
      </div>
    </div>
  );
};

export default function RegistrationPage() {
  const [createStudent] = useCreateStudentMutation();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    emailId: '',
    college: '',
    phoneNumber: '',
    branch: '',
    leadSource: 'WEBSITE'
  });
  const handleRegistrationSuccess = async (data: RegistrationData) => {
    try {
      await createStudent(data).unwrap();
      toast.success('Registration successful!');
      setFormData({
        name: '',
        emailId: '',
        college: '',
        phoneNumber: '',
        branch: '',
        leadSource: 'WEBSITE'
      });
    } catch (err) {
      toast.error(
        (err as any)?.data?.message || 'Registration failed. Please try again.'
      );
    }
  };

  return (
    <>
      <RegistrationForm
        formData={formData}
        setFormData={setFormData}
        onSuccess={handleRegistrationSuccess}
      />
    </>
  );
}
